import { createContext, useContext } from 'react';

import { LicenseEventsJoinMetadata } from '@common-lib';

export interface MetadataContextState {
  /**
   * Unique identifier for the device or browser. This is automatically
   * generated and does not use any device/os/browser specific data to
   * generate it. User can remove it which will generate a new one on
   * next app start so it cannot be used to identify specific users or
   * devices.
   */
  deviceId: string;
  organizationId: string;
  operatorId: string;
  portalId: string;
  portalDisplayName: string;

  /**
   * Only available in native apps, on web it fallbacks to 'web'
   */
  appId: string;

  /**
   * App Store numeric ID configured for this portal.
   */
  iosAppStoreId: string | undefined;

  /**
   * Only available in native apps, on web it fallbacks to 'web'
   */
  appName: string;

  /**
   * Only available in native apps, on web it fallbacks to 'web'
   */
  appBuild: string;

  /**
   * On native apps this is the version of the app installed by the user.
   * On web this is the version provided by APP_CONFIG.version.
   */
  appVersion: string;

  /**
   * Version as defined in APP_CONFIG.version.
   * On native apps this will be the version of the bundle updated and might not be the same as the app version.
   */
  bundleVersion: string;

  url: string;
  environment: string;
  stack: string;
  osVersion: string;
  androidSDK: number | undefined;
  manufacturer: string;
  model: string;
  platform: LicenseEventsJoinMetadata['platform'];
  languageTag: string;
  preferredTextZoom: number | undefined;
}

export const MetadataContext = createContext<MetadataContextState | undefined>(
  undefined,
);

export const MetadataProvider = MetadataContext.Provider;

export const useMetadata = (): MetadataContextState => {
  const context = useContext(MetadataContext);

  if (!context) {
    throw new Error('useMetadata must be used within a MetadataProvider');
  }

  return context;
};
